import { __extends } from "tslib";
import { ColorTransform, AssetEvent, AssetBase } from '@awayjs/core';
import { BlendMode, ImageBase } from '@awayjs/stage';
import { MaterialEvent, Style, StyleEvent } from '@awayjs/renderer';
/**
 * MaterialBase forms an abstract base class for any material.
 * A material consists of several passes, each of which constitutes at least one render call. Several passes could
 * be used for special effects (render lighting for many lights in several passes, render an outline in a separate
 * pass) or to provide additional render-to-texture passes (rendering diffuse light to texture for texture-space
 * subsurface scattering, or rendering a depth map for specialized self-shadowing).
 *
 * Away3D provides default materials trough SinglePassMaterialBase and TriangleMaterial, which use modular
 * methods to build the shader code. MaterialBase can be extended to build specific and high-performant custom
 * shaders, or entire new material frameworks.
*/
var MaterialBase = /** @class */ (function (_super) {
    __extends(MaterialBase, _super);
    function MaterialBase(imageColor, alpha) {
        if (imageColor === void 0) { imageColor = 0xFFFFFF; }
        if (alpha === void 0) { alpha = 1; }
        var _this = _super.call(this) || this;
        _this._textures = new Array();
        _this._pUseColorTransform = false;
        _this._alphaBlending = false;
        _this._alpha = 1;
        _this._pAlphaThreshold = 0;
        _this._pAnimateUVs = false;
        _this._style = new Style();
        _this._iBaseScreenPassIndex = 0;
        _this._bothSides = false; // update
        _this._pBlendMode = BlendMode.NORMAL;
        _this._imageRect = false;
        _this._curves = false;
        _this._onInvalidatePropertiesDelegate = function (event) { return _this._onInvalidateProperties(event); };
        _this._style.addEventListener(StyleEvent.INVALIDATE_PROPERTIES, _this._onInvalidatePropertiesDelegate);
        if (imageColor instanceof ImageBase)
            _this._style.image = imageColor;
        else
            _this._style.color = Number(imageColor);
        _this.alpha = alpha;
        _this._onTextureInvalidateDelegate = function (event) { return _this.onTextureInvalidate(event); };
        return _this;
    }
    Object.defineProperty(MaterialBase.prototype, "alpha", {
        /**
         * The alpha of the surface.
         */
        get: function () {
            return this._alpha;
        },
        set: function (value) {
            if (value > 1)
                value = 1;
            else if (value < 0)
                value = 0;
            if (this._alpha == value)
                return;
            this._alpha = value;
            if (this._colorTransform == null)
                this._colorTransform = new ColorTransform();
            this._colorTransform.alphaMultiplier = value;
            this.invalidate();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(MaterialBase.prototype, "colorTransform", {
        /**
         * The ColorTransform object to transform the colour of the material with. Defaults to null.
         */
        get: function () {
            return this._colorTransform;
        },
        set: function (value) {
            this._colorTransform = value;
            this.invalidate();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(MaterialBase.prototype, "alphaBlending", {
        /**
         * Indicates whether or not the material has transparency. If binary transparency is sufficient, for
         * example when using textures of foliage, consider using alphaThreshold instead.
         */
        get: function () {
            return this._alphaBlending;
        },
        set: function (value) {
            if (this._alphaBlending == value)
                return;
            this._alphaBlending = value;
            this.invalidate();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(MaterialBase.prototype, "curves", {
        /**
         * Indicates whether material should use curves. Defaults to false.
         */
        get: function () {
            return this._curves;
        },
        set: function (value) {
            if (this._curves == value)
                return;
            this._curves = value;
            this.invalidatePasses();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(MaterialBase.prototype, "imageRect", {
        /**
         * Indicates whether or not any used textures should use an atlas. Defaults to false.
         */
        get: function () {
            return this._imageRect;
        },
        set: function (value) {
            if (this._imageRect == value)
                return;
            this._imageRect = value;
            this.invalidatePasses();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(MaterialBase.prototype, "style", {
        /**
         * The style used to render the current TriangleGraphic. If set to null, its parent Sprite's style will be used instead.
         */
        get: function () {
            return this._style;
        },
        set: function (value) {
            if (this._style == value)
                return;
            if (this._style)
                this._style.removeEventListener(StyleEvent.INVALIDATE_PROPERTIES, this._onInvalidatePropertiesDelegate);
            this._style = value;
            if (this._style)
                this._style.addEventListener(StyleEvent.INVALIDATE_PROPERTIES, this._onInvalidatePropertiesDelegate);
            this.invalidatePasses();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(MaterialBase.prototype, "animateUVs", {
        /**
         * Specifies whether or not the UV coordinates should be animated using a transformation matrix.
         */
        get: function () {
            return this._pAnimateUVs;
        },
        set: function (value) {
            if (this._pAnimateUVs == value)
                return;
            this._pAnimateUVs = value;
            this.invalidatePasses();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(MaterialBase.prototype, "useColorTransform", {
        /**
         * Specifies whether or not the UV coordinates should be animated using a transformation matrix.
         */
        get: function () {
            return this._pUseColorTransform;
        },
        set: function (value) {
            if (this._pUseColorTransform == value)
                return;
            this._pUseColorTransform = value;
            this.invalidatePasses();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(MaterialBase.prototype, "bothSides", {
        /**
         * Defines whether or not the material should cull triangles facing away from the camera.
         */
        get: function () {
            return this._bothSides;
        },
        set: function (value) {
            if (this._bothSides == value)
                return;
            this._bothSides = value;
            this.invalidatePasses();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(MaterialBase.prototype, "blendMode", {
        /**
         * The blend mode to use when drawing this renderable. The following blend modes are supported:
         * <ul>
         * <li>BlendMode.NORMAL: No blending, unless the material inherently needs it</li>
         * <li>BlendMode.LAYER: Force blending. This will draw the object the same as NORMAL, but without writing depth writes.</li>
         * <li>BlendMode.MULTIPLY</li>
         * <li>BlendMode.ADD</li>
         * <li>BlendMode.ALPHA</li>
         * </ul>
         */
        get: function () {
            return this._pBlendMode;
        },
        set: function (value) {
            if (this._pBlendMode == value)
                return;
            this._pBlendMode = value;
            this.invalidate();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(MaterialBase.prototype, "alphaThreshold", {
        /**
         * The minimum alpha value for which pixels should be drawn. This is used for transparency that is either
         * invisible or entirely opaque, often used with textures for foliage, etc.
         * Recommended values are 0 to disable alpha, or 0.5 to create smooth edges. Default value is 0 (disabled).
         */
        get: function () {
            return this._pAlphaThreshold;
        },
        set: function (value) {
            if (value < 0)
                value = 0;
            else if (value > 1)
                value = 1;
            if (this._pAlphaThreshold == value)
                return;
            this._pAlphaThreshold = value;
            this.invalidatePasses();
        },
        enumerable: true,
        configurable: true
    });
    MaterialBase.prototype.getNumTextures = function () {
        return this._textures.length;
    };
    MaterialBase.prototype.getTextureAt = function (index) {
        return this._textures[index];
    };
    MaterialBase.prototype.dispose = function () {
        this.clear();
    };
    /**
     * Marks the shader programs for all passes as invalid, so they will be recompiled before the next use.
     *
     * @private
     */
    MaterialBase.prototype.invalidatePasses = function () {
        this.dispatchEvent(new MaterialEvent(MaterialEvent.INVALIDATE_PASSES, this));
    };
    MaterialBase.prototype.invalidateTextures = function () {
        this.dispatchEvent(new MaterialEvent(MaterialEvent.INVALIDATE_TEXTURES, this));
    };
    MaterialBase.prototype.addTextureAt = function (texture, index) {
        var i = this._textures.indexOf(texture);
        if (i == index)
            return;
        else if (i != -1)
            this._textures.splice(i, 1);
        this._textures.splice(index, 0, texture);
        texture.addEventListener(AssetEvent.INVALIDATE, this._onTextureInvalidateDelegate);
        this.onTextureInvalidate();
    };
    MaterialBase.prototype.addTexture = function (texture) {
        if (this._textures.indexOf(texture) != -1)
            return;
        this._textures.push(texture);
        texture.addEventListener(AssetEvent.INVALIDATE, this._onTextureInvalidateDelegate);
        this.onTextureInvalidate();
    };
    MaterialBase.prototype.removeTexture = function (texture) {
        this._textures.splice(this._textures.indexOf(texture), 1);
        texture.removeEventListener(AssetEvent.INVALIDATE, this._onTextureInvalidateDelegate);
        this.onTextureInvalidate();
    };
    MaterialBase.prototype.onTextureInvalidate = function (event) {
        if (event === void 0) { event = null; }
        this.invalidatePasses();
        this.invalidateTextures();
    };
    MaterialBase.prototype._onInvalidateProperties = function (event) {
        this.invalidatePasses();
        this.invalidateTextures();
    };
    return MaterialBase;
}(AssetBase));
export { MaterialBase };
import { _Render_MaterialPassBase, ShaderBase } from '@awayjs/renderer';
/**
 * _Render_DepthMaterial forms an abstract base class for the default shaded materials provided by Stage,
 * using material methods to define their appearance.
 */
var _Render_DepthMaterial = /** @class */ (function (_super) {
    __extends(_Render_DepthMaterial, _super);
    /**
     *
     * @param pool
     * @param surface
     * @param elementsClass
     * @param stage
     */
    function _Render_DepthMaterial(material, renderElements) {
        var _this = _super.call(this, material, renderElements) || this;
        _this._shader = new ShaderBase(renderElements, _this, _this, _this._stage);
        _this._pAddPass(_this);
        _this.invalidate();
        return _this;
    }
    _Render_DepthMaterial.prototype.invalidate = function () {
        _super.prototype.invalidate.call(this);
        var texture = this._material.getTextureAt(0);
        this._shaderTexture = texture ? texture.getAbstraction(this._shader) : null;
    };
    _Render_DepthMaterial.prototype._includeDependencies = function (shader) {
        _super.prototype._includeDependencies.call(this, shader);
        shader.projectionDependencies++;
        if (shader.alphaThreshold > 0)
            shader.uvDependencies++;
    };
    _Render_DepthMaterial.prototype._initConstantData = function () {
        var index = this._fragmentConstantsIndex;
        var data = this._shader.fragmentConstantData;
        data[index] = 1.0;
        data[index + 1] = 255.0;
        data[index + 2] = 65025.0;
        data[index + 3] = 16581375.0;
        data[index + 4] = 1.0 / 255.0;
        data[index + 5] = 1.0 / 255.0;
        data[index + 6] = 1.0 / 255.0;
        data[index + 7] = 0.0;
    };
    /**
     * @inheritDoc
     */
    _Render_DepthMaterial.prototype._getFragmentCode = function (registerCache, sharedRegisters) {
        var code = '';
        var targetReg = sharedRegisters.shadedTarget;
        var dataReg1 = registerCache.getFreeFragmentConstant();
        var dataReg2 = registerCache.getFreeFragmentConstant();
        this._fragmentConstantsIndex = dataReg1.index * 4;
        var temp1 = registerCache.getFreeFragmentVectorTemp();
        registerCache.addFragmentTempUsages(temp1, 1);
        var temp2 = registerCache.getFreeFragmentVectorTemp();
        registerCache.addFragmentTempUsages(temp2, 1);
        code += 'div ' + temp1 + ', ' + sharedRegisters.projectionFragment + ', ' + sharedRegisters.projectionFragment + '.w\n' + //"sub ft2.z, fc0.x, ft2.z\n" +    //invert
            'mul ' + temp1 + ', ' + dataReg1 + ', ' + temp1 + '.z\n' +
            'frc ' + temp1 + ', ' + temp1 + '\n' +
            'mul ' + temp2 + ', ' + temp1 + '.yzww, ' + dataReg2 + '\n';
        //codeF += "mov ft1.w, fc1.w	\n" +
        //    "mov ft0.w, fc0.x	\n";
        if (this._shaderTexture && this._shader.alphaThreshold > 0) {
            var albedo = registerCache.getFreeFragmentVectorTemp();
            code += this._shaderTexture._getFragmentCode(albedo, registerCache, sharedRegisters, sharedRegisters.uvVarying);
            var cutOffReg = registerCache.getFreeFragmentConstant();
            code += 'sub ' + albedo + '.w, ' + albedo + '.w, ' + cutOffReg + '.x\n' +
                'kil ' + albedo + '.w\n';
        }
        code += 'sub ' + targetReg + ', ' + temp1 + ', ' + temp2 + '\n';
        //DEBUG OPTION: MAKE DEPTHTEXTURES VISIBLE
        //code += "sub " + targetReg + ".xyz, " + temp1 + ".xyz, " + temp2 + ".xyz\n";
        //code += "mov " + targetReg + ".w, " + dataReg1 + ".x\n";
        registerCache.removeFragmentTempUsage(temp1);
        registerCache.removeFragmentTempUsage(temp2);
        return code;
    };
    /**
     * @inheritDoc
     */
    _Render_DepthMaterial.prototype._activate = function () {
        _super.prototype._activate.call(this);
        if (this._shaderTexture && this._shader.alphaThreshold > 0) {
            this._shaderTexture.activate();
            this._shader.fragmentConstantData[this._fragmentConstantsIndex + 8] = this._shader.alphaThreshold;
        }
    };
    return _Render_DepthMaterial;
}(_Render_MaterialPassBase));
export { _Render_DepthMaterial };
/**
 * DistanceRender is a pass that writes distance values to a depth map as a 32-bit value exploded over the 4 texture channels.
 * This is used to render omnidirectional shadow maps.
 */
var _Render_DistanceMaterial = /** @class */ (function (_super) {
    __extends(_Render_DistanceMaterial, _super);
    /**
     * Creates a new DistanceRender object.
     *
     * @param material The material to which this pass belongs.
     */
    function _Render_DistanceMaterial(material, renderElements) {
        var _this = _super.call(this, material, renderElements) || this;
        _this._shader = new ShaderBase(renderElements, _this, _this, _this._stage);
        _this._pAddPass(_this);
        _this.invalidate();
        return _this;
    }
    _Render_DistanceMaterial.prototype.invalidate = function () {
        _super.prototype.invalidate.call(this);
        var texture = this._material.getTextureAt(0);
        this._shaderTexture = texture ? texture.getAbstraction(this._shader) : null;
    };
    /**
     * Initializes the unchanging constant data for this material.
     */
    _Render_DistanceMaterial.prototype._initConstantData = function () {
        var index = this._fragmentConstantsIndex;
        var data = this._shader.fragmentConstantData;
        data[index + 4] = 1.0 / 255.0;
        data[index + 5] = 1.0 / 255.0;
        data[index + 6] = 1.0 / 255.0;
        data[index + 7] = 0.0;
    };
    _Render_DistanceMaterial.prototype._includeDependencies = function (shader) {
        _super.prototype._includeDependencies.call(this, shader);
        shader.projectionDependencies++;
        shader.viewDirDependencies++;
        if (shader.alphaThreshold > 0)
            shader.uvDependencies++;
        if (shader.viewDirDependencies > 0)
            shader.globalPosDependencies++;
    };
    /**
     * @inheritDoc
     */
    _Render_DistanceMaterial.prototype._getFragmentCode = function (registerCache, sharedRegisters) {
        var code;
        var targetReg = sharedRegisters.shadedTarget;
        var dataReg1 = registerCache.getFreeFragmentConstant();
        var dataReg2 = registerCache.getFreeFragmentConstant();
        this._fragmentConstantsIndex = dataReg1.index * 4;
        var temp1 = registerCache.getFreeFragmentVectorTemp();
        registerCache.addFragmentTempUsages(temp1, 1);
        var temp2 = registerCache.getFreeFragmentVectorTemp();
        registerCache.addFragmentTempUsages(temp2, 1);
        // squared distance to view
        code = 'dp3 ' + temp1 + '.z, ' + sharedRegisters.viewDirVarying + '.xyz, ' + sharedRegisters.viewDirVarying + '.xyz\n' +
            'mul ' + temp1 + ', ' + dataReg1 + ', ' + temp1 + '.z\n' +
            'frc ' + temp1 + ', ' + temp1 + '\n' +
            'mul ' + temp2 + ', ' + temp1 + '.yzww, ' + dataReg2 + '\n';
        if (this._shaderTexture && this._shader.alphaThreshold > 0) {
            var albedo = registerCache.getFreeFragmentVectorTemp();
            code += this._shaderTexture._getFragmentCode(albedo, registerCache, sharedRegisters, sharedRegisters.uvVarying);
            var cutOffReg = registerCache.getFreeFragmentConstant();
            code += 'sub ' + albedo + '.w, ' + albedo + '.w, ' + cutOffReg + '.x\n' +
                'kil ' + albedo + '.w\n';
        }
        code += 'sub ' + targetReg + ', ' + temp1 + ', ' + temp2 + '\n';
        return code;
    };
    /**
     * @inheritDoc
     */
    _Render_DistanceMaterial.prototype._activate = function () {
        _super.prototype._activate.call(this);
        var f = this._shader.view.projection.far;
        f = 1 / (2 * f * f);
        // sqrt(f*f+f*f) is largest possible distance for any frustum, so we need to divide by it. Rarely a tight fit, but with 32 bits precision, it's enough.
        var index = this._fragmentConstantsIndex;
        var data = this._shader.fragmentConstantData;
        data[index] = 1.0 * f;
        data[index + 1] = 255.0 * f;
        data[index + 2] = 65025.0 * f;
        data[index + 3] = 16581375.0 * f;
        if (this._shaderTexture && this._shader.alphaThreshold > 0) {
            this._shaderTexture.activate();
            data[index + 8] = this._shader.alphaThreshold;
        }
    };
    return _Render_DistanceMaterial;
}(_Render_MaterialPassBase));
export { _Render_DistanceMaterial };
